import type { CaseDocument, DocumentType } from '@core/domain/model';
import { failure, guard, type DataResult } from '@core/domain/result';
import { newId, toDocument } from './mappers';
import type { Database } from './sqlite';

const SORT_STEP = 1024;

/**
 * Избранное идёт первым **внутри любой сортировки**, включая свою — [D-20].
 * Одно выражение на всё приложение: в библиотеке, в превью карточки дела и в поиске.
 */
const FAVORITE_FIRST = 'isFavorite DESC';

export function documentRepository(db: Database, now: () => number = Date.now) {
  const find = async (id: string) =>
    (await db.query('SELECT * FROM documents WHERE id = ?', [id]))[0] ?? null;

  const touch = async (id: string, sql: string, params: (string | number | null)[]) => {
    if (!(await find(id))) return failure({ kind: 'NotFound' as const, id });
    return guard(async () => {
      await db.run(sql, [...params, now(), id]);
    });
  };

  return {
    async ofCase(caseId: string): Promise<CaseDocument[]> {
      const rows = await db.query(
        `SELECT * FROM documents
          WHERE caseId = ? AND deletedAt IS NULL
          ORDER BY ${FAVORITE_FIRST}, sortOrder`,
        [caseId],
      );
      return rows.map(toDocument);
    },

    async ofClient(clientId: string): Promise<CaseDocument[]> {
      const rows = await db.query(
        `SELECT * FROM documents
          WHERE clientId = ? AND deletedAt IS NULL
          ORDER BY ${FAVORITE_FIRST}, addedAt DESC`,
        [clientId],
      );
      return rows.map(toDocument);
    },

    async find(id: string): Promise<CaseDocument | null> {
      const row = await find(id);
      return row && row.deletedAt === null ? toDocument(row) : null;
    },

    /** Кладёт документ в дело. Файл к этому моменту уже лежит в хранилище. */
    async add(
      draft: Omit<CaseDocument, 'id' | 'sortOrder' | 'addedAt' | 'createdAt' | 'updatedAt'> & {
        id?: string;
      },
    ) {
      return guard(async () => {
        const id = draft.id?.trim() ? draft.id : newId();
        const at = now();

        const [last] = await db.query<{ next: number }>(
          'SELECT COALESCE(MAX(sortOrder), 0) + ? AS next FROM documents WHERE caseId = ?',
          [SORT_STEP, draft.caseId],
        );

        await db.run(
          `INSERT INTO documents (id, title, caseId, clientId, fileName, mimeType, sizeBytes,
                                  pageCount, isFavorite, type, source, sortOrder, addedAt,
                                  createdAt, updatedAt, deletedAt)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            id,
            draft.title,
            draft.caseId,
            draft.clientId,
            draft.fileName,
            draft.mimeType,
            draft.sizeBytes,
            draft.pageCount,
            draft.isFavorite ? 1 : 0,
            draft.type,
            draft.source,
            Number(last?.next ?? SORT_STEP),
            at,
            at,
            at,
          ],
        );
        return id;
      });
    },

    rename: (id: string, title: string) =>
      touch(id, 'UPDATE documents SET title = ?, updatedAt = ? WHERE id = ?', [title]),

    setFavorite: (id: string, isFavorite: boolean) =>
      touch(id, 'UPDATE documents SET isFavorite = ?, updatedAt = ? WHERE id = ?', [
        isFavorite ? 1 : 0,
      ]),

    setType: (id: string, type: DocumentType) =>
      touch(id, 'UPDATE documents SET type = ?, updatedAt = ? WHERE id = ?', [type]),

    /** Привязка к клиенту дополнительно к делу: доверенность относится к человеку. */
    setClient: (id: string, clientId: string | null) =>
      touch(id, 'UPDATE documents SET clientId = ?, updatedAt = ? WHERE id = ?', [clientId]),

    setPageCount: (id: string, pageCount: number) =>
      touch(id, 'UPDATE documents SET pageCount = ?, updatedAt = ? WHERE id = ?', [pageCount]),

    /** Замена содержимого: название, тип, порядок и избранное остаются прежними. */
    replaceContent: (id: string, fileName: string, mimeType: string, sizeBytes: number) =>
      touch(
        id,
        `UPDATE documents SET fileName = ?, mimeType = ?, sizeBytes = ?, pageCount = NULL,
                              updatedAt = ? WHERE id = ?`,
        [fileName, mimeType, sizeBytes],
      ),

    /**
     * Порядок документов дела пишется **целиком**, ровными шагами.
     *
     * Не «поставить между соседями»: середина между двумя одинаковыми значениями равна
     * им же, и порядок разваливался с первого переноса — разбор в PITFALLS §13.
     */
    async reorder(caseId: string, orderedIds: string[]): Promise<DataResult<void>> {
      return guard(async () => {
        await db.transaction(async (tx) => {
          for (const [index, id] of orderedIds.entries()) {
            await tx.run('UPDATE documents SET sortOrder = ? WHERE id = ? AND caseId = ?', [
              (index + 1) * SORT_STEP,
              id,
              caseId,
            ]);
          }
        });
      });
    },

    /** Мягкое удаление: запись скрывается, файл остаётся — пока можно «Отменить». */
    remove: (id: string) =>
      touch(id, 'UPDATE documents SET deletedAt = ?, updatedAt = ? WHERE id = ?', [now()]),

    restore: (id: string) =>
      touch(id, 'UPDATE documents SET deletedAt = NULL, updatedAt = ? WHERE id = ?', []),

    /** Сколько живых документов всего — для проверки хранилища. */
    async countLive(): Promise<number> {
      const [row] = await db.query<{ total: number }>(
        'SELECT COUNT(*) AS total FROM documents WHERE deletedAt IS NULL',
      );
      return Number(row?.total ?? 0);
    },

    /**
     * Файлы живых документов живых дел, по делам.
     *
     * Нужны уборке хранилища: всё, чего нет в этом наборе, на диске лишнее.
     */
    async liveFilesByCase(): Promise<Map<string, Set<string>>> {
      const rows = await db.query<{ caseId: string; fileName: string }>(
        `SELECT d.caseId AS caseId, d.fileName AS fileName
           FROM documents d JOIN cases c ON c.id = d.caseId
          WHERE d.deletedAt IS NULL AND c.deletedAt IS NULL AND d.caseId IS NOT NULL`,
      );

      const files = new Map<string, Set<string>>();
      for (const row of rows) {
        const set = files.get(row.caseId) ?? new Set<string>();
        set.add(row.fileName);
        files.set(row.caseId, set);
      }
      return files;
    },
  };
}

export type DocumentRepository = ReturnType<typeof documentRepository>;
